import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  Star, Package, ShoppingCart, Palette, DraftingCompass, Settings2,
  BadgeCheck, Crown, LogOut, Shield, Download, Clock3, Truck,
  Mail, Calendar, ArrowRight, PenSquare, Store, FileBox, Trash2, ShoppingBag,
  File as FileIcon, Image as ImageIcon, FileText as FileTextIcon,
  UserCog, Rocket, BookCopy
} from 'lucide-react';
import Footer from './Footer';
import MainNavigation from './MainNavigation';

const Profile = ({ navigateHome, navigateToPage }) => {
  const { user, isAuthenticated, logout, loading, getIdToken, refreshUserProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [designerApplication, setDesignerApplication] = useState(null);
  const [loadingDesignerStatus, setLoadingDesignerStatus] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  // Review composer state
  const [reviewTarget, setReviewTarget] = useState(null); // { product_id, product_name }
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewedProductIds, setReviewedProductIds] = useState([]);
  // Section-based navigation for the profile page
  const [activeSection, setActiveSection] = useState('quick-actions');
  // My Parts & Files dashboard state
  const [myFiles, setMyFiles] = useState([]);
  const [myFileCounts, setMyFileCounts] = useState({ all: 0, cad: 0, '2d': 0, other: 0 });
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileFilter, setFileFilter] = useState('all');
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Format a currency amount to two decimals with thousand separators.
  // Safely falls back to $0.00 when value is missing or NaN.
  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '$0.00';
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Fetch user orders when component mounts
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserOrders();
      fetchDesignerApplicationStatus();
      fetchUserPurchases();
      fetchMyFiles();
    }
  }, [isAuthenticated, user]);

  // ---------- My Parts & Files ----------
  const fetchMyFiles = async () => {
    setLoadingFiles(true);
    try {
      const idToken = await getIdToken();
      if (!idToken) return;
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiUrl}/api/me/files`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyFiles(data.files || []);
        setMyFileCounts(data.counts || { all: 0, cad: 0, '2d': 0, other: 0 });
      }
    } catch (err) {
      console.error('Fetch my files error:', err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDeleteFile = async (file) => {
    setDeletingFileId(`${file.source}/${file.source_id}`);
    try {
      const idToken = await getIdToken();
      if (!idToken) return;
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiUrl}/api/me/files/${file.source}/${file.source_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (res.ok) {
        setFileToDelete(null);
        await fetchMyFiles();
      } else {
        alert('Could not remove that file. Please try again.');
      }
    } catch (err) {
      console.error('Delete file error:', err);
      alert('Could not remove that file. Please try again.');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDownloadMyFile = async (file) => {
    try {
      const idToken = await getIdToken();
      if (!idToken) return;
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiUrl}/api/me/files/${file.source}/${file.source_id}/download`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (!res.ok) {
        alert('This file is no longer available.');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed.');
    }
  };

  const handleOrderPart = (file) => {
    // Route to Instant Quote tool with file preloaded via reuse params.
    // We pass `source`, `reuse` (id), and `name` so the tool can reconstruct
    // the file with its real filename + extension (needed for preview typing).
    const params = new URLSearchParams({
      service: 'on-demand-3d-print',
      source: file.source,
      reuse: file.source_id,
      name: file.filename || '',
    });
    window.location.href = `/get-started?${params.toString()}`;
  };

  const fileIconFor = (type) => {
    if (type === 'cad') return FileBox;
    if (type === '2d') return ImageIcon;
    return FileTextIcon;
  };

  const filteredFiles = fileFilter === 'all'
    ? myFiles
    : myFiles.filter(f => f.file_type === fileFilter);

  const fetchDesignerApplicationStatus = async () => {
    setLoadingDesignerStatus(true);
    try {
      const idToken = await getIdToken();
      if (!idToken) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      // Check user's specific designer application status
      const appResponse = await fetch(`${apiUrl}/api/marketplace/my-designer-application`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
      });

      if (appResponse.ok) {
        const appData = await appResponse.json();
        setDesignerApplication(appData.has_application ? appData.application : null);
      } else {
        setDesignerApplication(null);
      }
    } catch (error) {
      console.error('Error fetching designer status:', error);
      setDesignerApplication(null);
    } finally {
      setLoadingDesignerStatus(false);
    }
  };

  const fetchUserOrders = async () => {
    setLoadingOrders(true);
    try {
      // Get Firebase ID token for authenticated request
      const idToken = await getIdToken();
      if (!idToken) {
        console.error('Unable to get authentication token');
        setLoadingOrders(false);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/orders/my-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
        // Fetch the products this user has already reviewed so we can hide the button
        try {
          const revRes = await fetch(`${apiUrl}/api/me/reviews`, {
            headers: { 'Authorization': `Bearer ${idToken}` },
            credentials: 'include',
          });
          if (revRes.ok) {
            const revData = await revRes.json();
            setReviewedProductIds(revData.product_ids || []);
          }
        } catch (_) { /* non-critical */ }
      } else {
        console.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch user's marketplace purchases
  const fetchUserPurchases = async () => {
    setLoadingPurchases(true);
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        setLoadingPurchases(false);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/marketplace/my-purchases`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setPurchases(data.purchases || []);
      } else {
        console.error('Failed to fetch purchases');
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoadingPurchases(false);
    }
  };

  // Handle download for purchased designs
  const handleDownload = async (purchaseId) => {
    setDownloadingId(purchaseId);
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        alert('Please sign in to download');
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/marketplace/purchases/${purchaseId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.download_url) {
          // Construct full URL for download
          let downloadUrl = data.download_url;
          // If it's a relative URL, prepend the backend URL
          if (!downloadUrl.startsWith('http')) {
            downloadUrl = `${apiUrl}${downloadUrl}`;
          }
          // Open download in new tab
          window.open(downloadUrl, '_blank');
          // Refresh purchases to update download count
          fetchUserPurchases();
        } else {
          alert('Download URL not available');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to download');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      alert('Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  // Helper function to determine order type
  const getOrderType = (order) => {
    // Product orders have product_name and no quote_id
    if (order.product_name && !order.quote_id) {
      return 'product';
    }
    // Printing orders have quote_id or filename that doesn't start with "Cart Purchase"
    return 'printing';
  };

  // Handle navigation to specific sections
  const navigateToQuote = () => {
    // Navigate to home page with hash to instant quote section
    window.location.href = '/#instant-quote';
  };

  const navigateToServices = () => {
    // Navigate to services page
    navigateToPage('services');
  };
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  // Open the review composer for a specific product order
  const openReviewFor = (order, e) => {
    if (e) e.stopPropagation();
    setReviewForm({ rating: 5, title: '', comment: '' });
    setReviewTarget({
      product_id: order.product_id,
      product_name: order.product_name || 'this product',
    });
  };

  // Submit the review to the backend
  const submitReview = async () => {
    if (!reviewTarget || !reviewTarget.product_id) return;
    if (!reviewForm.title.trim() || !reviewForm.comment.trim()) {
      alert('Please add a title and comment.');
      return;
    }
    setSubmittingReview(true);
    try {
      const idToken = await getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const apiUrl = (!backendUrl || backendUrl.includes('None')) ? '' : backendUrl;
      const res = await fetch(`${apiUrl}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          product_id: reviewTarget.product_id,
          rating: Number(reviewForm.rating),
          title: reviewForm.title.trim(),
          comment: reviewForm.comment.trim(),
        }),
      });
      if (res.ok) {
        setReviewedProductIds(prev => [...prev, reviewTarget.product_id]);
        setReviewTarget(null);
        alert('Thanks for your review!');
      } else {
        let msg = 'Unable to submit review. Please try again.';
        try {
          const err = await res.json();
          if (typeof err.detail === 'string') msg = err.detail;
        } catch { /* ignore */ }
        alert(msg);
      }
    } catch (err) {
      console.error('submit review error', err);
      alert('Unable to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Component for product order display
  const ProductOrderCard = ({ order }) => (
    <div 
      className="border border-slate-200 rounded-xl p-5 hover:border-[#0007b5]/30 hover:shadow-sm transition-all cursor-pointer bg-white"
      onClick={() => handleOrderClick(order)}
      data-testid={`product-order-card-${order.id}`}
    >
      <div className="flex justify-between items-start mb-4 gap-3">
        <div className="min-w-0">
          <h4 className="font-bold text-slate-900 truncate">{order.product_name}</h4>
          <p className="text-xs text-slate-500 mt-0.5">#{order.id.slice(0, 8)} · Product Purchase</p>
        </div>
        {getStatusBadge(order.status)}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Manufacturer</p>
          <p className="font-semibold text-slate-900 mt-0.5">{order.product_manufacturer || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Category</p>
          <p className="font-semibold text-slate-900 mt-0.5">{order.product_category || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Quantity</p>
          <p className="font-semibold text-slate-900 mt-0.5">{order.quantity || 1}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Price</p>
          <p className="font-semibold text-emerald-700 mt-0.5">{fmtMoney(order.final_price || order.estimated_price)}</p>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center gap-3 flex-wrap">
        <p className="text-xs text-slate-500">
          Ordered {order.created_at ? (() => {
            try {
              const date = new Date(order.created_at);
              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
            } catch (e) {
              return 'N/A';
            }
          })() : 'N/A'}
        </p>
        <div className="flex items-center gap-3">
          {order.product_id && ['confirmed','in_production','quality_check','shipping','completed'].includes(order.status) && (
            reviewedProductIds.includes(order.product_id) ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
                <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" /> Review submitted
              </span>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="text-[#0007b5] border-[#0007b5]/30 hover:bg-[#0007b5]/5"
                onClick={(e) => openReviewFor(order, e)}
                data-testid={`write-review-btn-${order.id}`}
              >
                <PenSquare className="w-3.5 h-3.5 mr-1" /> Write a Review
              </Button>
            )
          )}
          {order.tracking_number && (
            <p className="inline-flex items-center gap-1 text-xs text-[#0007b5] font-medium">
              <Truck className="w-3 h-3" /> {order.tracking_number}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Component for printing order display
  const PrintingOrderCard = ({ order }) => (
    <div 
      className="border border-slate-200 rounded-xl p-5 hover:border-[#0007b5]/30 hover:shadow-sm transition-all cursor-pointer bg-white"
      onClick={() => handleOrderClick(order)}
      data-testid={`printing-order-card-${order.id}`}
    >
      <div className="flex justify-between items-start mb-4 gap-3">
        <div className="min-w-0">
          <h4 className="font-bold text-slate-900 truncate">{order.filename}</h4>
          <p className="text-xs text-slate-500 mt-0.5">#{order.id.slice(0, 8)} · 3D Printing Service</p>
        </div>
        {getStatusBadge(order.status)}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Material</p>
          <p className="font-semibold text-slate-900 mt-0.5">{order.material}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Quality</p>
          <p className="font-semibold text-slate-900 mt-0.5">{order.quality}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Price</p>
          <p className="font-semibold text-emerald-700 mt-0.5">{fmtMoney(order.estimated_price)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Delivery</p>
          <p className="font-semibold text-slate-900 mt-0.5">{order.delivery_weeks} weeks</p>
        </div>
      </div>
      
      {/* Additional printing details */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4 text-xs text-slate-500">
        <div><span className="text-slate-400">Color:</span> <span className="text-slate-700 font-medium">{order.color}</span></div>
        <div><span className="text-slate-400">Strength:</span> <span className="text-slate-700 font-medium">{order.strength}</span></div>
        <div><span className="text-slate-400">Smoothing:</span> <span className="text-slate-700 font-medium">{order.smoothing}</span></div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center gap-3 flex-wrap">
        <p className="text-xs text-slate-500">
          Created {order.created_at ? (() => {
            try {
              const date = new Date(order.created_at);
              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
            } catch (e) {
              return 'N/A';
            }
          })() : 'N/A'}
        </p>
        {order.tracking_number && (
          <p className="inline-flex items-center gap-1 text-xs text-[#0007b5] font-medium">
            <Truck className="w-3 h-3" /> {order.tracking_number}
          </p>
        )}
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-50 text-amber-700 border border-amber-200', text: 'Pending' },
      pending_review: { color: 'bg-amber-50 text-amber-700 border border-amber-200', text: 'Pending Review' },
      confirmed: { color: 'bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/20', text: 'Confirmed' },
      in_production: { color: 'bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/20', text: 'In Production' },
      quality_check: { color: 'bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/20', text: 'Quality Check' },
      shipping: { color: 'bg-sky-50 text-sky-700 border border-sky-200', text: 'Shipping' },
      completed: { color: 'bg-emerald-50 text-emerald-700 border border-emerald-200', text: 'Completed' },
      cancelled: { color: 'bg-red-50 text-red-700 border border-red-200', text: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="profile-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="profile-access-denied">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#0007b5]/5 border border-[#0007b5]/15 flex items-center justify-center mx-auto mb-5">
            <Shield className="w-7 h-7 text-[#0007b5]" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Sign in required</h2>
          <p className="text-slate-600 mb-8">You need to be signed in to view your profile and order history.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-[#0007b5] text-white py-3 px-4 rounded-xl hover:bg-[#000595] transition-colors font-semibold"
            data-testid="access-denied-return-home-btn"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      {/* Header */}
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') window.location.href = '/';
          else if (page === 'profile') window.location.href = '/profile';
          else window.location.href = `/${page}`;
        }} 
        currentPage="profile" 
        cartCount={0}
      />

      {/* Hero Banner */}
      <section className="relative bg-[#0007b5] text-white overflow-hidden" data-testid="profile-hero">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
            backgroundSize: '56px 56px'
          }}
        />
        {/* Decorative glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Left: avatar + name */}
            <div className="flex items-center gap-5 min-w-0">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || 'User'}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border-4 border-white/20 shadow-lg object-cover flex-shrink-0"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <div
                  className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/15 border-4 border-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg flex-shrink-0"
                  data-testid="profile-avatar-initial"
                >
                  <span className="text-3xl md:text-4xl font-black text-white">
                    {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-blue-200 text-xs font-semibold tracking-widest uppercase mb-1">My Profile</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-heading truncate" data-testid="profile-name">
                  {user.name || 'Welcome'}
                </h1>
                <div className="flex items-center gap-2 mt-2 text-blue-100 text-sm">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate" title={user.email} data-testid="profile-email">{user.email}</span>
                </div>
              </div>
            </div>

            {/* Right: status pills */}
            <div className="flex flex-wrap gap-2 md:justify-end">
              {user.email_verified && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/20" data-testid="badge-email-verified">
                  <BadgeCheck className="w-3.5 h-3.5" /> Email Verified
                </span>
              )}
              {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' || user?.is_admin) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-400 text-amber-950 shadow-sm" data-testid="badge-admin">
                  <Crown className="w-3.5 h-3.5" /> Admin
                </span>
              )}
              {user?.is_designer && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-400 text-emerald-950 shadow-sm" data-testid="badge-designer">
                  <Palette className="w-3.5 h-3.5" /> Verified Designer
                </span>
              )}
              {!user?.is_designer && designerApplication?.status === 'pending' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/15 text-white border border-white/20" data-testid="badge-designer-pending">
                  <Clock3 className="w-3.5 h-3.5" /> Designer Application Pending
                </span>
              )}
              {user.created_at && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 text-blue-100 border border-white/15" data-testid="badge-member-since">
                  <Calendar className="w-3.5 h-3.5" /> Member since {(() => {
                    try {
                      const date = new Date(user.created_at);
                      return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                    } catch (e) { return 'N/A'; }
                  })()}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Profile Content — Option A: Unified panel (sidebar + content in one card) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16 relative -mt-6 md:-mt-8">

        {/* Unified panel card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)]">
          <div className="grid grid-cols-1 lg:grid-cols-12">
          
          {/* Sidebar — tinted column inside the unified card */}
          <aside className="lg:col-span-4 xl:col-span-3 bg-slate-50/60 lg:border-r border-slate-200 rounded-t-2xl lg:rounded-tr-none lg:rounded-l-2xl lg:self-start lg:sticky lg:top-6">
            <div className="p-5 space-y-3">

              {/* ONE-TIME: Promote to Super Admin */}
              {user.email && user.email.toLowerCase().includes('pitlanesimracing') &&
               user?.role !== 'SUPER_ADMIN' && user?.role !== 'ADMIN' && !user?.is_admin ? (
                <button
                  onClick={async () => {
                    if (!confirm('Click OK to promote yourself to Super Admin. This only needs to be done once.')) return;
                    try {
                      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                      const response = await fetch(`${apiUrl}/api/auth/promote-to-superadmin`, { method: 'POST', credentials: 'include' });
                      const data = await response.json();
                      if (response.ok) { alert(data.message); window.location.reload(); }
                      else { alert('Error: ' + (data.detail || data.message || 'Unknown error')); }
                    } catch (error) { alert('Error promoting to admin: ' + error.message); }
                  }}
                  className="w-full bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                  data-testid="promote-superadmin-btn"
                >
                  <Crown className="w-4 h-4" />
                  Activate Admin Access
                </button>
              ) : null}

              {/* Mobile section selector */}
              <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-slate-200 p-3">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide px-1 mb-1">Browse</label>
                <select
                  value={activeSection}
                  onChange={(e) => setActiveSection(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5] font-medium text-slate-900"
                  data-testid="profile-section-select-mobile"
                >
                  <option value="quick-actions">Quick Actions</option>
                  <option value="orders">Recent Orders</option>
                  <option value="purchases">Design Purchases</option>
                  <option value="files">My Parts &amp; Files</option>
                  <option value="settings">Account Settings</option>
                </select>
              </div>

              {/* Desktop navigation menu — flat, lives inside the unified card */}
              <nav className="hidden lg:block" data-testid="profile-section-nav">
                <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-500 mb-3 px-2">Your Account</p>

                {/* Section links */}
                <div>
                {[
                  { id: 'quick-actions', icon: Rocket,      label: 'Quick Actions',    sub: 'Shortcuts',               count: null },
                  { id: 'orders',        icon: Package,     label: 'Recent Orders',    sub: 'Prints & products',       count: orders.length },
                  { id: 'purchases',     icon: BookCopy,    label: 'Design Purchases', sub: 'Downloads & print orders', count: purchases.length },
                  { id: 'files',         icon: FileBox,     label: 'My Parts & Files', sub: 'Uploaded files',          count: myFiles.length },
                  { id: 'settings',      icon: UserCog,     label: 'Account Settings', sub: 'Profile & preferences',   count: null },
                ].map(item => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors mb-1 ${
                        active
                          ? 'bg-[#0007b5]/5 text-[#0007b5]'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`section-nav-${item.id}`}
                    >
                      <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        active
                          ? 'bg-[#0007b5] text-white shadow-sm'
                          : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-sm truncate">{item.label}</span>
                      </span>
                      {item.count !== null && item.count > 0 && (
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          active ? 'bg-[#0007b5] text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {item.count}
                        </span>
                      )}
                    </button>
                  );
                })}
                </div>

                {/* Divider with label */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 px-2 mb-2">Shortcuts</p>
                </div>

                <div>
                  {/* Admin Panel */}
                  {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' || user?.is_admin) && (
                    <button
                      onClick={() => navigateToPage('admin')}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-[#0007b5] hover:bg-[#0007b5]/5 transition-colors mb-1 group"
                      data-testid="admin-panel-btn"
                    >
                      <span className="flex items-center gap-3">
                        <Crown className="w-4 h-4" />
                        Admin Panel
                      </span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  )}

                  {/* Designer Dashboard */}
                  {user?.is_designer && (
                    <button
                      onClick={() => navigateToPage('designer/dashboard')}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors mb-1 group"
                      data-testid="designer-dashboard-btn"
                    >
                      <span className="flex items-center gap-3">
                        <Store className="w-4 h-4" />
                        Designer Dashboard
                      </span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  )}

                  {/* Become a Designer */}
                  {!user?.is_designer && !designerApplication && !loadingDesignerStatus && (
                    <button
                      onClick={() => navigateToPage('designer/apply')}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors mb-1 group"
                      data-testid="apply-designer-btn"
                    >
                      <span className="flex items-center gap-3">
                        <Palette className="w-4 h-4 text-[#0007b5]" />
                        Become a Designer
                      </span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  )}

                  {/* Sign Out */}
                  <button
                    onClick={async () => { await logout(); navigateHome(); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                    data-testid="sign-out-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </nav>

              {/* Mobile-only trailing actions (match desktop nav divider items) */}
              <div className="lg:hidden space-y-2">
                {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' || user?.is_admin) && (
                  <button
                    onClick={() => navigateToPage('admin')}
                    className="w-full bg-white border-2 border-[#0007b5] text-[#0007b5] hover:bg-[#0007b5] hover:text-white font-semibold py-3 px-4 rounded-xl transition-all flex items-center justify-between"
                    data-testid="admin-panel-btn-mobile"
                  >
                    <span className="flex items-center gap-2"><Crown className="w-4 h-4" /> Admin Panel</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                {user?.is_designer && (
                  <button
                    onClick={() => navigateToPage('designer/dashboard')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2"
                    data-testid="designer-dashboard-btn-mobile"
                  >
                    <Store className="w-4 h-4" /> Designer Dashboard
                  </button>
                )}
                <button
                  onClick={async () => { await logout(); navigateHome(); }}
                  className="w-full bg-white border border-slate-200 text-slate-700 hover:border-red-300 hover:text-red-600 font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                  data-testid="sign-out-btn-mobile"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>

            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-8 xl:col-span-9 p-6 md:p-8">
            {(() => {
              const meta = {
                'quick-actions': { title: 'Quick Actions',    Icon: Rocket },
                'orders':        { title: 'Recent Orders',    Icon: Package },
                'purchases':     { title: 'Design Purchases', Icon: BookCopy },
                'files':         { title: 'My Parts & Files', Icon: FileBox },
                'settings':      { title: 'Account Settings', Icon: UserCog },
              }[activeSection] || {};
              const Icon = meta.Icon;
              return (
                <div className="flex items-center gap-3 pb-5 mb-6 border-b border-slate-100" data-testid="active-section-header">
                  {Icon && (
                    <div className="w-11 h-11 rounded-xl bg-[#0007b5]/5 border border-[#0007b5]/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#0007b5]" />
                    </div>
                  )}
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight truncate">{meta.title}</h2>
                </div>
              );
            })()}

            <div className="space-y-6">
              
              {/* Quick Actions */}
              {activeSection === 'quick-actions' && (
              <div data-testid="quick-actions-card">
                {user?.is_designer && (
                  <div className="flex justify-end mb-4">
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Designer Account</Badge>
                  </div>
                )}
                
                {/* Designer Welcome Message */}
                {user?.is_designer && (
                  <div className="bg-[#0007b5]/5 border border-[#0007b5]/15 rounded-xl p-5 mb-6" data-testid="designer-welcome-banner">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white border border-emerald-100 flex items-center justify-center flex-shrink-0">
                        <BadgeCheck className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900">Welcome to the Designer Program</h4>
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                          Your application has been approved. Start uploading designs and building your storefront.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={navigateToQuote}
                    className="group p-5 bg-white border border-slate-200 rounded-xl hover:border-[#0007b5]/30 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
                    data-testid="quick-action-quote"
                  >
                    <div className="w-11 h-11 bg-[#0007b5]/5 border border-[#0007b5]/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0007b5]/10 transition-colors">
                      <DraftingCompass className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div className="font-bold text-slate-900 mb-1">Get a Quote</div>
                    <div className="text-sm text-slate-600 leading-snug">Upload 3D files for instant pricing</div>
                  </button>

                  <button
                    onClick={navigateToServices}
                    className="group p-5 bg-white border border-slate-200 rounded-xl hover:border-[#0007b5]/30 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
                    data-testid="quick-action-services"
                  >
                    <div className="w-11 h-11 bg-[#0007b5]/5 border border-[#0007b5]/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0007b5]/10 transition-colors">
                      <Settings2 className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div className="font-bold text-slate-900 mb-1">Services</div>
                    <div className="text-sm text-slate-600 leading-snug">Design and optimization services</div>
                  </button>

                  <button
                    onClick={() => navigateToPage('marketplace')}
                    className="group p-5 bg-white border border-slate-200 rounded-xl hover:border-[#0007b5]/30 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
                    data-testid="quick-action-marketplace"
                  >
                    <div className="w-11 h-11 bg-[#0007b5]/5 border border-[#0007b5]/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0007b5]/10 transition-colors">
                      <ShoppingCart className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div className="font-bold text-slate-900 mb-1">Browse Designs</div>
                    <div className="text-sm text-slate-600 leading-snug">Explore the 3D design marketplace</div>
                  </button>

                  {user?.is_designer ? (
                    <button
                      onClick={() => navigateToPage('designer/dashboard')}
                      className="group p-5 bg-emerald-50/60 border border-emerald-200 rounded-xl hover:border-emerald-400 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
                      data-testid="quick-action-designer-dashboard"
                    >
                      <div className="w-11 h-11 bg-white border border-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                        <Store className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="font-bold text-slate-900 mb-1">Designer Dashboard</div>
                      <div className="text-sm text-slate-600 leading-snug">Manage designs & earnings</div>
                    </button>
                  ) : (
                    <button
                      onClick={() => navigateToPage('designer/apply')}
                      className="group p-5 bg-white border border-slate-200 rounded-xl hover:border-[#0007b5]/30 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
                      data-testid="quick-action-become-designer"
                    >
                      <div className="w-11 h-11 bg-[#0007b5]/5 border border-[#0007b5]/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0007b5]/10 transition-colors">
                        <Palette className="w-5 h-5 text-[#0007b5]" />
                      </div>
                      <div className="font-bold text-slate-900 mb-1">Become a Designer</div>
                      <div className="text-sm text-slate-600 leading-snug">Sell your 3D designs</div>
                    </button>
                  )}
                </div>
              </div>
              )}

              {/* Order History */}
              {activeSection === 'orders' && (
              <div data-testid="recent-orders-card">
                {loadingOrders ? (
                  <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0007b5] mx-auto mb-3"></div>
                    <p className="text-slate-600 text-sm">Loading your orders...</p>
                  </div>
                ) : orders.length > 0 ? (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const orderType = getOrderType(order);
                      return orderType === 'product' ? (
                        <ProductOrderCard key={order.id} order={order} />
                      ) : (
                        <PrintingOrderCard key={order.id} order={order} />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 px-4 border border-dashed border-slate-200 rounded-xl" data-testid="orders-empty-state">
                    <div className="w-14 h-14 rounded-2xl bg-[#0007b5]/5 border border-[#0007b5]/15 flex items-center justify-center mx-auto mb-4">
                      <Package className="w-7 h-7 text-[#0007b5]" />
                    </div>
                    <p className="text-lg font-bold text-slate-900 mb-1">No orders yet</p>
                    <p className="text-sm text-slate-600 mb-5 max-w-sm mx-auto">Your quotes, prints, and service requests will appear here once you place your first order.</p>
                    <button
                      onClick={navigateToQuote}
                      className="inline-flex items-center gap-2 bg-[#0007b5] text-white py-2.5 px-5 rounded-lg hover:bg-[#000595] transition-colors font-semibold text-sm"
                      data-testid="start-first-order-btn"
                    >
                      Start Your First Order
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* My Purchases - Design Downloads */}
              {activeSection === 'purchases' && (
              <div data-testid="my-design-purchases-card">
                
                {loadingPurchases ? (
                  <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0007b5] mx-auto mb-3"></div>
                    <p className="text-slate-600 text-sm">Loading your purchases...</p>
                  </div>
                ) : purchases.length > 0 ? (
                  <div className="space-y-4">
                    {purchases.map((purchase) => (
                      <div key={purchase.id} className="border border-slate-200 rounded-xl p-4 hover:border-[#0007b5]/30 hover:shadow-sm transition-all" data-testid={`purchase-row-${purchase.id}`}>
                        <div className="flex items-start gap-4">
                          {/* Thumbnail */}
                          <div className="w-20 h-20 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0 border border-slate-200">
                            {purchase.listing?.thumbnail ? (
                              <img 
                                src={purchase.listing.thumbnail} 
                                alt={purchase.listing?.title || 'Design'}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-400">
                                <Palette className="w-7 h-7" />
                              </div>
                            )}
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-slate-900 truncate">
                              {purchase.listing?.title || 'Design'}
                            </h4>
                            <p className="text-sm text-slate-500">
                              by {purchase.listing?.designer_name || 'Designer'}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/15">
                                {purchase.license_type}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                ${purchase.purchase_price?.toFixed(2)}
                              </span>
                              {purchase.purchase_type === 'print' && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                  <Truck className="w-3 h-3" /> Print Order
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              Purchased: {new Date(purchase.created_at).toLocaleDateString()}
                              {purchase.download_count > 0 && ` • Downloaded ${purchase.download_count} time${purchase.download_count !== 1 ? 's' : ''}`}
                            </p>
                          </div>
                          
                          {/* Download Button */}
                          {purchase.purchase_type === 'download' && (
                            <button
                              onClick={() => handleDownload(purchase.id)}
                              disabled={downloadingId === purchase.id}
                              className="flex-shrink-0 bg-[#0007b5] text-white px-4 py-2 rounded-lg hover:bg-[#000595] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold text-sm"
                              data-testid={`download-purchase-${purchase.id}`}
                            >
                              {downloadingId === purchase.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  <span>Loading...</span>
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4" />
                                  <span>Download</span>
                                </>
                              )}
                            </button>
                          )}
                          
                          {/* Print Order Status */}
                          {purchase.purchase_type === 'print' && (
                            <div className="flex-shrink-0 text-center">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                purchase.order_status === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                purchase.order_status === 'shipping' ? 'bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/15' :
                                purchase.order_status === 'in_production' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                'bg-slate-100 text-slate-700 border border-slate-200'
                              }`}>
                                {purchase.order_status === 'completed' ? 'Completed' :
                                 purchase.order_status === 'shipping' ? 'Shipping' :
                                 purchase.order_status === 'in_production' ? 'In Production' :
                                 'Processing'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 px-4 border border-dashed border-slate-200 rounded-xl" data-testid="purchases-empty-state">
                    <div className="w-14 h-14 rounded-2xl bg-[#0007b5]/5 border border-[#0007b5]/15 flex items-center justify-center mx-auto mb-4">
                      <Palette className="w-7 h-7 text-[#0007b5]" />
                    </div>
                    <p className="text-lg font-bold text-slate-900 mb-1">No purchases yet</p>
                    <p className="text-sm text-slate-600 mb-5 max-w-sm mx-auto">Your purchased designs will appear here for easy download anytime.</p>
                    <button
                      onClick={() => navigateToPage('marketplace')}
                      className="inline-flex items-center gap-2 bg-[#0007b5] text-white py-2.5 px-5 rounded-lg hover:bg-[#000595] transition-colors font-semibold text-sm"
                      data-testid="browse-designs-btn"
                    >
                      Browse Designs
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* My Parts & Files */}
              {activeSection === 'files' && (
              <div data-testid="my-parts-files-card">
                <div className="flex items-center justify-end mb-5 flex-wrap gap-3">
                  {myFileCounts.all > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="file-filter-chips">
                      {[
                        { key: 'all',   label: 'All',   n: myFileCounts.all },
                        { key: 'cad',   label: 'CAD',   n: myFileCounts.cad },
                        { key: '2d',    label: '2D',    n: myFileCounts['2d'] },
                        { key: 'other', label: 'Other', n: myFileCounts.other },
                      ].filter(f => f.n > 0 || f.key === 'all').map(f => (
                        <button
                          key={f.key}
                          onClick={() => setFileFilter(f.key)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            fileFilter === f.key
                              ? 'bg-[#0007b5] text-white border-[#0007b5]'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`file-filter-${f.key}`}
                        >
                          {f.label} <span className={fileFilter === f.key ? 'opacity-80' : 'text-slate-400'}>({f.n})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {loadingFiles ? (
                  <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0007b5] mx-auto mb-3"></div>
                    <p className="text-slate-600 text-sm">Loading your files...</p>
                  </div>
                ) : filteredFiles.length > 0 ? (
                  <div className="space-y-3">
                    {filteredFiles.map((file) => {
                      const Icon = fileIconFor(file.file_type);
                      const rowKey = `${file.source}/${file.source_id}`;
                      return (
                        <div
                          key={rowKey}
                          className="border border-slate-200 rounded-xl p-4 hover:border-[#0007b5]/30 hover:shadow-sm transition-all"
                          data-testid={`file-row-${rowKey}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#0007b5]/5 border border-[#0007b5]/15 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-5 h-5 text-[#0007b5]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 flex-wrap">
                                <h4 className="font-bold text-slate-900 truncate" title={file.filename}>{file.filename}</h4>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${
                                  file.file_type === 'cad'
                                    ? 'bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/15'
                                    : file.file_type === '2d'
                                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                      : 'bg-slate-100 text-slate-700 border border-slate-200'
                                }`}>
                                  {file.file_type === '2d' ? '2D' : file.file_type}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate">
                                {file.label}
                                {file.size_human ? ` · ${file.size_human}` : ''}
                                {file.created_at
                                  ? ` · ${(() => { try { return new Date(file.created_at).toLocaleDateString(); } catch (e) { return ''; } })()}`
                                  : ''}
                              </p>
                              {file.description && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2" title={file.description}>
                                  {file.description}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                              {file.can_order_part && (
                                <Button
                                  size="sm"
                                  className="bg-[#0007b5] hover:bg-[#000595] text-white font-semibold"
                                  onClick={() => handleOrderPart(file)}
                                  data-testid={`order-part-btn-${rowKey}`}
                                >
                                  <ShoppingBag className="w-4 h-4 mr-1" />
                                  Order Part
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-200 text-slate-700 hover:border-[#0007b5] hover:text-[#0007b5]"
                                onClick={() => handleDownloadMyFile(file)}
                                data-testid={`download-file-btn-${rowKey}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600"
                                onClick={() => setFileToDelete(file)}
                                data-testid={`delete-file-btn-${rowKey}`}
                                aria-label="Remove file"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : myFiles.length === 0 ? (
                  <div className="text-center py-12 px-4 border border-dashed border-slate-200 rounded-xl" data-testid="my-files-empty-state">
                    <div className="w-14 h-14 rounded-2xl bg-[#0007b5]/5 border border-[#0007b5]/15 flex items-center justify-center mx-auto mb-4">
                      <FileBox className="w-7 h-7 text-[#0007b5]" />
                    </div>
                    <p className="text-lg font-bold text-slate-900 mb-1">No uploaded files yet</p>
                    <p className="text-sm text-slate-600 mb-5 max-w-sm mx-auto">
                      Files you upload for quotes or service requests will show up here so you can reorder them without re-uploading.
                    </p>
                    <button
                      onClick={navigateToQuote}
                      className="inline-flex items-center gap-2 bg-[#0007b5] text-white py-2.5 px-5 rounded-lg hover:bg-[#000595] transition-colors font-semibold text-sm"
                      data-testid="upload-first-file-btn"
                    >
                      Upload Your First Part
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500 text-sm">
                    No files match this filter.
                  </div>
                )}
              </div>
              )}

              {/* Account Settings - Full Implementation */}
              {activeSection === 'settings' && (
                <AccountSettings user={user} getIdToken={getIdToken} />
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Delete-file confirmation */}
      <Dialog open={!!fileToDelete} onOpenChange={(open) => { if (!open) setFileToDelete(null); }}>
        <DialogContent className="max-w-md bg-white text-slate-900 border border-slate-200 shadow-2xl" data-testid="delete-file-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Remove file from your dashboard?
            </DialogTitle>
            <DialogDescription>
              We'll hide <strong className="text-slate-900">{fileToDelete?.filename}</strong> from your Parts &amp; Files list. Any order placed with this file stays in your history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-3">
            <Button
              variant="outline"
              onClick={() => setFileToDelete(null)}
              data-testid="delete-file-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => fileToDelete && handleDeleteFile(fileToDelete)}
              disabled={!!deletingFileId}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-file-confirm-btn"
            >
              {deletingFileId ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer review composer */}
      <Dialog open={!!reviewTarget} onOpenChange={(open) => { if (!open) setReviewTarget(null); }}>
        <DialogContent className="max-w-lg bg-white text-slate-900 border border-slate-200 shadow-2xl" data-testid="customer-review-dialog">
          {reviewTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Review {reviewTarget.product_name}
                </DialogTitle>
                <DialogDescription>
                  Your review will be shown publicly on the product page with your name.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Rating</label>
                  <div className="flex items-center gap-1" data-testid="customer-review-rating">
                    {[1,2,3,4,5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                        className="p-1"
                        aria-label={`${n} star${n === 1 ? '' : 's'}`}
                      >
                        <Star className={`w-6 h-6 ${n <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                      </button>
                    ))}
                    <span className="text-sm text-slate-500 ml-2">{reviewForm.rating} / 5</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Title</label>
                  <Input
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Great printer for the price"
                    data-testid="customer-review-title"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Your review</label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0007b5]/30"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder="What did you like or dislike? How did you use it? Any tips for other buyers?"
                    data-testid="customer-review-comment"
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setReviewTarget(null)}>Cancel</Button>
                <Button onClick={submitReview} disabled={submittingReview} data-testid="customer-review-submit">
                  {submittingReview ? 'Submitting…' : 'Submit review'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Order Details</h2>
                  <p className="text-slate-600">Order #{selectedOrder.id.slice(0, 8)}...</p>
                </div>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Order Type and Status */}
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {getOrderType(selectedOrder) === 'product' ? selectedOrder.product_name : selectedOrder.filename}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {getOrderType(selectedOrder) === 'product' ? 'Product Purchase' : '3D Printing Service'}
                    </p>
                  </div>
                  {getStatusBadge(selectedOrder.status)}
                </div>

                {/* Order Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {getOrderType(selectedOrder) === 'product' ? (
                    <>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Product Details</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Manufacturer:</span> {selectedOrder.product_manufacturer || 'N/A'}</p>
                          <p><span className="text-slate-500">Category:</span> {selectedOrder.product_category || 'N/A'}</p>
                          <p><span className="text-slate-500">Quantity:</span> {selectedOrder.quantity || 1}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Order Summary</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Price:</span> {fmtMoney(selectedOrder.final_price || selectedOrder.estimated_price)}</p>
                          <p><span className="text-slate-500">Ordered:</span> {selectedOrder.created_at ? (() => {
                            try {
                              const date = new Date(selectedOrder.created_at);
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                            } catch (e) {
                              return 'N/A';
                            }
                          })() : 'N/A'}</p>
                          {selectedOrder.tracking_number && (
                            <p><span className="text-slate-500">Tracking:</span> {selectedOrder.tracking_number}</p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Printing Specifications</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Material:</span> {selectedOrder.material}</p>
                          <p><span className="text-slate-500">Quality:</span> {selectedOrder.quality}</p>
                          <p><span className="text-slate-500">Color:</span> {selectedOrder.color}</p>
                          <p><span className="text-slate-500">Strength:</span> {selectedOrder.strength}</p>
                          <p><span className="text-slate-500">Smoothing:</span> {selectedOrder.smoothing}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Order Summary</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Price:</span> {fmtMoney(selectedOrder.estimated_price)}</p>
                          <p><span className="text-slate-500">Delivery:</span> {selectedOrder.delivery_weeks} weeks</p>
                          <p><span className="text-slate-500">Created:</span> {selectedOrder.created_at ? (() => {
                            try {
                              const date = new Date(selectedOrder.created_at);
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                            } catch (e) {
                              return 'N/A';
                            }
                          })() : 'N/A'}</p>
                          {selectedOrder.tracking_number && (
                            <p><span className="text-slate-500">Tracking:</span> {selectedOrder.tracking_number}</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Customer Notes */}
                {selectedOrder.customer_notes && selectedOrder.customer_notes !== "Cart purchase" && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Notes</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {selectedOrder.customer_notes}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowOrderDetails(false)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ACCOUNT SETTINGS COMPONENT - Shipping, Billing, Email Notifications
// ============================================================================

const AccountSettings = ({ user, getIdToken }) => {
  const [activeTab, setActiveTab] = useState('shipping');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Shipping Addresses State
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
    is_default: false
  });

  // Payment Methods State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);

  // Email Preferences State
  const [emailPreferences, setEmailPreferences] = useState({
    order_confirmation: true,
    order_updates: true,
    shipping_notifications: true,
    delivery_notifications: true,
    marketing_emails: true,
    newsletter: true,
    promotions: true,
    designer_application_updates: true,
    design_sales_notifications: true,
    payout_notifications: true,
    account_security_alerts: true,
    password_changes: true
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const apiUrl = backendUrl.includes('None') || !backendUrl ? '' : backendUrl;

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'shipping') {
      loadAddresses();
    } else if (activeTab === 'billing') {
      loadPaymentMethods();
    } else if (activeTab === 'email') {
      loadEmailPreferences();
    }
  }, [activeTab]);

  // ========== SHIPPING ADDRESSES FUNCTIONS ==========
  
  const loadAddresses = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/shipping-addresses`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAddresses(data.addresses);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const idToken = await getIdToken();
      
      // Validate address first if US or Canada
      if (addressForm.country === 'US' || addressForm.country === 'CA') {
        const validateResponse = await fetch(`${apiUrl}/api/validate-address`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            address_line1: addressForm.address_line1,
            address_line2: addressForm.address_line2,
            city: addressForm.city,
            state_province: addressForm.state,
            postal_code: addressForm.postal_code,
            country: addressForm.country
          })
        });

        if (validateResponse.ok) {
          const validationResult = await validateResponse.json();
          if (!validationResult.valid) {
            setMessage({ 
              type: 'error', 
              text: 'Address validation failed: ' + validationResult.errors.join(', ')
            });
            setLoading(false);
            return;
          }
          
          // Use normalized address if provided
          if (validationResult.normalized_address) {
            addressForm.address_line1 = validationResult.normalized_address.address_line1;
            addressForm.city = validationResult.normalized_address.city;
            addressForm.state = validationResult.normalized_address.state_province;
            addressForm.postal_code = validationResult.normalized_address.postal_code;
          }
        }
      }
      
      const url = editingAddress
        ? `${apiUrl}/api/user/shipping-addresses/${editingAddress.id}`
        : `${apiUrl}/api/user/shipping-addresses`;
      
      const method = editingAddress ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addressForm)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Address ${editingAddress ? 'updated' : 'added'} successfully!` });
        setShowAddressForm(false);
        setEditingAddress(null);
        resetAddressForm();
        await loadAddresses();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save address' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving address' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/shipping-addresses/${addressId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Address deleted successfully!' });
        await loadAddresses();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting address' });
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      name: address.name,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country,
      phone: address.phone || '',
      is_default: address.is_default
    });
    setShowAddressForm(true);
  };

  const resetAddressForm = () => {
    setAddressForm({
      name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      phone: '',
      is_default: false
    });
  };

  // ========== PAYMENT METHODS FUNCTIONS ==========
  
  const loadPaymentMethods = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/payment-methods`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.payment_methods);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const handleSetDefaultPayment = async (methodId) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/payment-methods/${methodId}/set-default`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Default payment method updated!' });
        await loadPaymentMethods();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating default payment method' });
    }
  };

  const handleDeletePayment = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;

    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/payment-methods/${methodId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Payment method deleted successfully!' });
        await loadPaymentMethods();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting payment method' });
    }
  };

  // ========== EMAIL PREFERENCES FUNCTIONS ==========
  
  const loadEmailPreferences = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/email-preferences`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error loading email preferences:', error);
    }
  };

  const handleEmailPreferenceChange = async (key, value) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/email-preferences`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (response.ok) {
        setEmailPreferences(prev => ({ ...prev, [key]: value }));
        setMessage({ type: 'success', text: 'Email preferences updated!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating preferences' });
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/test-email-notification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      const data = await response.json();
      setMessage({ type: data.success ? 'success' : 'error', text: data.message });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error sending test email' });
    }
  };

  // ========== RENDER ==========

  return (
    <div>
      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('shipping')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'shipping'
                ? 'border-[#0007b5] text-[#0007b5]'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            📦 Shipping Addresses
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'billing'
                ? 'border-[#0007b5] text-[#0007b5]'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            💳 Billing Information
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'email'
                ? 'border-[#0007b5] text-[#0007b5]'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            📧 Email Notifications
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      <div className="pt-6">
        {/* SHIPPING ADDRESSES TAB */}
        {activeTab === 'shipping' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-900">Your Shipping Addresses</h4>
              <button
                onClick={() => {
                  resetAddressForm();
                  setEditingAddress(null);
                  setShowAddressForm(true);
                }}
                className="px-4 py-2 bg-[#0007b5] text-white rounded-lg hover:bg-[#000595] transition-colors"
              >
                + Add Address
              </button>
            </div>

            {/* Address Form */}
            {showAddressForm && (
              <form onSubmit={handleAddressSubmit} className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
                <h5 className="font-medium text-slate-900">{editingAddress ? 'Edit Address' : 'New Address'}</h5>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Name *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.name}
                      onChange={e => setAddressForm({...addressForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={addressForm.phone}
                      onChange={e => setAddressForm({...addressForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                  <input
                    type="text"
                    required
                    value={addressForm.address_line1}
                    onChange={e => setAddressForm({...addressForm, address_line1: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={addressForm.address_line2}
                    onChange={e => setAddressForm({...addressForm, address_line2: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.city}
                      onChange={e => setAddressForm({...addressForm, city: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">State/Province *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.state}
                      onChange={e => setAddressForm({...addressForm, state: e.target.value})}
                      placeholder="e.g., CA, ON"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ZIP/Postal Code *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.postal_code}
                      onChange={e => setAddressForm({...addressForm, postal_code: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                  <select
                    required
                    value={addressForm.country}
                    onChange={e => setAddressForm({...addressForm, country: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0007b5] focus:border-[#0007b5]"
                  >
                    <option value="US">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={addressForm.is_default}
                    onChange={e => setAddressForm({...addressForm, is_default: e.target.checked})}
                    className="w-4 h-4 text-[#0007b5] border-slate-300 rounded focus:ring-[#0007b5]"
                  />
                  <label htmlFor="is_default" className="ml-2 text-sm text-slate-700">
                    Set as default shipping address
                  </label>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-[#0007b5] text-white rounded-lg hover:bg-[#000595] transition-colors disabled:bg-slate-300"
                  >
                    {loading ? 'Saving...' : (editingAddress ? 'Update Address' : 'Add Address')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddressForm(false);
                      setEditingAddress(null);
                      resetAddressForm();
                    }}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Address List */}
            {addresses.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <div className="text-4xl mb-4">📦</div>
                <p>No shipping addresses saved</p>
                <p className="text-sm mt-2">Add an address to speed up your checkout process</p>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map(address => (
                  <div key={address.id} className="border border-slate-200 rounded-lg p-4 hover:border-[#0007b5]/30 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-slate-900">{address.name}</span>
                          {address.is_default && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p>{address.address_line1}</p>
                          {address.address_line2 && <p>{address.address_line2}</p>}
                          <p>{address.city}, {address.state} {address.postal_code}</p>
                          <p>{address.country}</p>
                          {address.phone && <p>Phone: {address.phone}</p>}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditAddress(address)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(address.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BILLING INFORMATION TAB */}
        {activeTab === 'billing' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-900">Your Payment Methods</h4>
              <button
                onClick={() => setShowAddPaymentForm(true)}
                className="px-4 py-2 bg-[#0007b5] text-white rounded-lg hover:bg-[#000595] transition-colors"
              >
                + Add Payment Method
              </button>
            </div>

            {/* Add Payment Method Notice */}
            {showAddPaymentForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">Add Payment Method</h5>
                <p className="text-sm text-blue-800 mb-3">
                  Payment method integration will be completed once Stripe API keys are provided.
                  This feature uses Stripe's secure payment processing to safely store your card information.
                </p>
                <button
                  onClick={() => setShowAddPaymentForm(false)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Close
                </button>
              </div>
            )}

            {/* Payment Methods List */}
            {paymentMethods.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <div className="text-4xl mb-4">💳</div>
                <p>No payment methods saved</p>
                <p className="text-sm mt-2">Add a payment method for faster checkout</p>
                <p className="text-xs mt-3 text-slate-400">Secure payment processing powered by Stripe</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map(method => (
                  <div key={method.id} className="border border-slate-200 rounded-lg p-4 hover:border-[#0007b5]/30 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-slate-900">
                            {method.card_brand.toUpperCase()} •••• {method.card_last4}
                          </span>
                          {method.is_default && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p>Expires: {method.card_exp_month}/{method.card_exp_year}</p>
                          <p>{method.billing_name}</p>
                          <p>{method.billing_address_line1}, {method.billing_city}, {method.billing_state} {method.billing_postal_code}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {!method.is_default && (
                          <button
                            onClick={() => handleSetDefaultPayment(method.id)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePayment(method.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EMAIL NOTIFICATIONS TAB */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-900">Email Notification Preferences</h4>
              <button
                onClick={handleSendTestEmail}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Send Test Email
              </button>
            </div>

            {/* Order Notifications */}
            <div>
              <h5 className="font-medium text-slate-900 mb-3">Order Notifications</h5>
              <div className="space-y-3">
                <EmailToggle
                  label="Order Confirmation"
                  description="Receive confirmation emails when you place an order"
                  checked={emailPreferences.order_confirmation}
                  onChange={(val) => handleEmailPreferenceChange('order_confirmation', val)}
                />
                <EmailToggle
                  label="Order Updates"
                  description="Get notified about order status changes"
                  checked={emailPreferences.order_updates}
                  onChange={(val) => handleEmailPreferenceChange('order_updates', val)}
                />
                <EmailToggle
                  label="Shipping Notifications"
                  description="Receive tracking info when your order ships"
                  checked={emailPreferences.shipping_notifications}
                  onChange={(val) => handleEmailPreferenceChange('shipping_notifications', val)}
                />
                <EmailToggle
                  label="Delivery Notifications"
                  description="Get notified when your order is delivered"
                  checked={emailPreferences.delivery_notifications}
                  onChange={(val) => handleEmailPreferenceChange('delivery_notifications', val)}
                />
              </div>
            </div>

            {/* Marketing Notifications */}
            <div>
              <h5 className="font-medium text-slate-900 mb-3">Marketing & Promotions</h5>
              <div className="space-y-3">
                <EmailToggle
                  label="Marketing Emails"
                  description="Receive promotional offers and product updates"
                  checked={emailPreferences.marketing_emails}
                  onChange={(val) => handleEmailPreferenceChange('marketing_emails', val)}
                />
                <EmailToggle
                  label="Newsletter"
                  description="Get our monthly newsletter with tips and news"
                  checked={emailPreferences.newsletter}
                  onChange={(val) => handleEmailPreferenceChange('newsletter', val)}
                />
                <EmailToggle
                  label="Special Promotions"
                  description="Be the first to know about sales and discounts"
                  checked={emailPreferences.promotions}
                  onChange={(val) => handleEmailPreferenceChange('promotions', val)}
                />
              </div>
            </div>

            {/* Designer Notifications (if user is a designer) */}
            {user.is_designer && (
              <div>
                <h5 className="font-medium text-slate-900 mb-3">Designer Notifications</h5>
                <div className="space-y-3">
                  <EmailToggle
                    label="Application Updates"
                    description="Status updates on your designer application"
                    checked={emailPreferences.designer_application_updates}
                    onChange={(val) => handleEmailPreferenceChange('designer_application_updates', val)}
                  />
                  <EmailToggle
                    label="Sales Notifications"
                    description="Get notified when someone purchases your designs"
                    checked={emailPreferences.design_sales_notifications}
                    onChange={(val) => handleEmailPreferenceChange('design_sales_notifications', val)}
                  />
                  <EmailToggle
                    label="Payout Notifications"
                    description="Receive updates about your earnings and payouts"
                    checked={emailPreferences.payout_notifications}
                    onChange={(val) => handleEmailPreferenceChange('payout_notifications', val)}
                  />
                </div>
              </div>
            )}

            {/* Account Security */}
            <div>
              <h5 className="font-medium text-slate-900 mb-3">Account Security</h5>
              <div className="space-y-3">
                <EmailToggle
                  label="Security Alerts"
                  description="Important security notifications (recommended)"
                  checked={emailPreferences.account_security_alerts}
                  onChange={(val) => handleEmailPreferenceChange('account_security_alerts', val)}
                />
                <EmailToggle
                  label="Password Changes"
                  description="Get notified when your password is changed"
                  checked={emailPreferences.password_changes}
                  onChange={(val) => handleEmailPreferenceChange('password_changes', val)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Email Toggle Component
const EmailToggle = ({ label, description, checked, onChange }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <div>
        <div className="font-medium text-slate-900">{label}</div>
        <div className="text-sm text-slate-600">{description}</div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-[#0007b5]' : 'bg-slate-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
};

export default Profile;
